/* ═══════════════════════════════════════════════════════════════
   Toolbar — week nav, group-by, copy-prev-week, mine-only, weekend
   toggle, filters, saved-views menu. Stateless about app data —
   takes config + handlers from the parent.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import type { Department, Project } from '../../../shared/types';
import type { DashboardView, Team } from '../../../shared/entities';
import { mondayOf, addDays } from '../../services/timeService';
import {
  type ViewConfig,
  type GroupBy,
  type Scope,
  type Breakdown,
  Modal,
  SegmentedControl,
  ToggleChip,
  btnGhost,
  btnPrimary,
  chipBtn,
  formatShort,
  navBtn,
  textInput,
  todayIso,
} from './_shared';
import { ViewsMenu } from './ViewsMenu';
import { FilterPanel } from './FilterPanel';

export function Toolbar({
  weekStart,
  onWeekChange,
  config,
  onConfigChange,
  departments,
  teams,
  projects,
  savedViews,
  activeViewId,
  onApplyView,
  onSaveView,
  onUpdateView,
  onDeleteView,
  onSetDefaultView,
  onRenameView,
  onShareToggle,
  currentUserId,
  canBulk,
  scopeOptions,
  onCopyPrevWeek,
  copying,
  viewIsDirty,
  searchQuery,
  onSearchChange,
}: {
  weekStart: string;
  onWeekChange: (next: string) => void;
  config: ViewConfig;
  onConfigChange: (next: ViewConfig) => void;
  departments: Department[];
  teams: Team[];
  projects: Project[];
  savedViews: DashboardView[];
  activeViewId: string | null;
  onApplyView: (v: DashboardView) => void;
  onSaveView: (name: string) => Promise<void>;
  onUpdateView: () => Promise<void>;
  onDeleteView: (id: string) => Promise<void>;
  onSetDefaultView: (id: string) => Promise<void>;
  onRenameView: (id: string, name: string) => Promise<void>;
  onShareToggle: (id: string) => Promise<void>;
  currentUserId: string;
  canBulk: boolean;
  /** Scope choices the user is allowed to pick (e.g. PM scope is hidden if
   *  the user PMs no projects; All is hidden without forecasting.view_all).
   *  Always includes 'mine'. */
  scopeOptions: { value: Scope; label: string; available: boolean }[];
  onCopyPrevWeek: () => Promise<void>;
  copying: boolean;
  viewIsDirty: boolean;
  /** Free-text query that fuzz-matches against project names/numbers,
   *  member names, team names, and department names. Ephemeral — not
   *  saved with views (search is a transient question, not a setting). */
  searchQuery: string;
  onSearchChange: (next: string) => void;
}) {
  const [showSaveView, setShowSaveView] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const weekLabel = `${formatShort(weekStart)} – ${formatShort(addDays(weekStart, 6))}`;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid var(--bd)',
        background: 'var(--bg)',
      }}
    >
      {/* Week navigation */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: 'var(--sa)',
          borderRadius: 6,
          padding: 2,
        }}
      >
        <button
          onClick={() => onWeekChange(addDays(weekStart, -7))}
          style={navBtn}
          aria-label="Previous week"
          title="Previous week"
        >
          ‹
        </button>
        <button
          onClick={() => onWeekChange(mondayOf(todayIso()))}
          style={{ ...navBtn, padding: '6px 12px', fontWeight: 600 }}
        >
          Today
        </button>
        <button
          onClick={() => onWeekChange(addDays(weekStart, 7))}
          style={navBtn}
          aria-label="Next week"
          title="Next week"
        >
          ›
        </button>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)', padding: '0 12px' }}>
          {weekLabel}
        </span>
      </div>

      {/* Group by — four pivots: Department / Team / Project / Flat. */}
      <SegmentedControl
        label="Group"
        value={config.groupBy}
        options={[
          { value: 'department', label: 'Dept' },
          { value: 'team', label: 'Team' },
          { value: 'project', label: 'Project' },
          { value: 'none', label: 'Flat' },
        ]}
        onChange={(v) => onConfigChange({ ...config, groupBy: v as GroupBy })}
      />

      {/* Project-view breakdown — only relevant when groupBy='project'.
          Lets a PM/lead pivot the per-project numbers across team / dept
          / individual without leaving the page. Defaults to Individual. */}
      {config.groupBy === 'project' && (
        <SegmentedControl
          label="Breakdown"
          value={config.projectBreakdown}
          options={[
            { value: 'individual', label: 'Person' },
            { value: 'team', label: 'Team' },
            { value: 'department', label: 'Dept' },
          ]}
          onChange={(v) => onConfigChange({ ...config, projectBreakdown: v as Breakdown })}
        />
      )}

      {/* Scope is now folded into the Filter panel — see the Filter button
          below for People (Mine / My team / My projects / All), Departments,
          Teams, and Projects in one place. */}

      {/* Global search — filters projects, members, teams, departments
          across whichever view is active. Ephemeral; not saved in views. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 220,
          maxWidth: 320,
          flex: '1 1 auto',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 12, color: 'var(--tm)' }}>
          🔎
        </span>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onSearchChange('');
            }
          }}
          placeholder="Search projects, people, teams…"
          aria-label="Search forecasting"
          style={{
            ...textInput,
            padding: '6px 10px',
            fontSize: 12,
            flex: 1,
          }}
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
            title="Clear (Esc)"
            style={{
              ...chipBtn,
              padding: '4px 8px',
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }} />

      {/* Copy previous week — bulk_assign action. Pulls every manual cell
          from the prior week and writes the same allocations into the
          currently-displayed week (idempotent — overwrites if the cell
          already exists). Permission-gated. */}
      {canBulk && (
        <button
          onClick={onCopyPrevWeek}
          disabled={copying}
          style={{ ...chipBtn, opacity: copying ? 0.5 : 1, cursor: copying ? 'wait' : 'pointer' }}
          title="Copy every manual forecast from the previous week into this week (existing cells get overwritten)."
        >
          {copying ? 'Copying…' : 'Copy prev week'}
        </button>
      )}

      {/* Show weekend */}
      <ToggleChip
        active={config.showWeekend}
        onClick={() => onConfigChange({ ...config, showWeekend: !config.showWeekend })}
        label="Weekend"
      />

      {/* Filters dropdown */}
      {(() => {
        // Active filter count = chip selections + non-default scope.
        // Scope counts as 1 when it's anything other than 'all', so the
        // user gets a clear "you've narrowed something" signal whether
        // they used a scope shortcut or explicit chips.
        const chipCount =
          config.selectedDepartmentIds.length +
          config.selectedTeamIds.length +
          config.selectedProjectIds.length;
        const scopeCount = config.scope !== 'all' ? 1 : 0;
        const activeCount = chipCount + scopeCount;
        const hasFilters = activeCount > 0;
        return (
          <button
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            style={{
              ...chipBtn,
              background: hasFilters
                ? 'color-mix(in srgb, var(--ac) 12%, transparent)'
                : 'var(--sa)',
              color: hasFilters ? 'var(--ac)' : 'var(--tx)',
              fontWeight: hasFilters ? 700 : 500,
            }}
            title="Filter by people, projects, teams, departments"
          >
            Filter
            {activeCount > 0 && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  background: 'var(--ac)',
                  color: '#fff',
                  borderRadius: 999,
                  padding: '1px 6px',
                  fontWeight: 700,
                }}
              >
                {activeCount}
              </span>
            )}
          </button>
        );
      })()}

      {/* Saved views dropdown — full CRUD: rename, delete, default, share. */}
      <ViewsMenu
        savedViews={savedViews}
        activeViewId={activeViewId}
        currentUserId={currentUserId}
        viewIsDirty={viewIsDirty}
        onApplyView={onApplyView}
        onSaveAs={() => setShowSaveView(true)}
        onUpdateActive={onUpdateView}
        onDeleteView={onDeleteView}
        onSetDefaultView={onSetDefaultView}
        onRenameView={onRenameView}
        onShareToggle={onShareToggle}
      />

      {/* Filter panel */}
      {showFilters && (
        <FilterPanel
          departments={departments}
          teams={teams}
          projects={projects}
          config={config}
          onConfigChange={onConfigChange}
          scopeOptions={scopeOptions}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Save view modal */}
      {showSaveView && (
        <Modal onClose={() => setShowSaveView(false)} title="Save current view">
          <input
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
            placeholder="View name (e.g. My Team This Quarter)"
            style={textInput}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowSaveView(false)} style={btnGhost}>
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!newViewName.trim()) return;
                await onSaveView(newViewName.trim());
                setNewViewName('');
                setShowSaveView(false);
              }}
              style={btnPrimary}
            >
              Save view
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
